import { useMemo } from 'react';
import { functionalUpdate, ExpandedState, SortingState } from '@tanstack/react-table';
import { VirtualizedTable, tableHelpers } from '@round/creatorbase-ui';
import { CreatorTableEmptyState } from './EmptyState/CreatorTableEmptyState';
import { ExpandedCreatorContent } from './components/ExpandedCreatorContent/ExpandedCreatorContent';
import { CreatorCell } from './cells/CreatorCell/CreatorCell';
import { LocationCell } from './cells/LocationCell/LocationCell';
import { ResponseRateCell } from './cells/ResponseRateCell/ResponseRateCell';
import { TagsCell } from './cells/TagsCell/TagsCell';
import { PlatformSelectorCell } from './cells/PlatformSelectorCell/PlatformSelectorCell';
import {
    AvgCpeCell,
    FollowersCell,
    CostCell,
    PlaysCell,
    CpmCell,
    EnagementRateCell,
    PostingFrequencyCell,
} from './cells/PlatformStatCells/PlatformStatCells';
import {
    Meta,
    PAGE_SIZE,
    Column,
    getPresetVisibilityStateFromColumns,
    DATE_ADDED_COLUMN_ID,
    getDateAddedValue,
    getDraftTimeValue,
    getLastCollabValue,
    getConnectedPlatforms,
    MATCH_SCORE_COLUMN_ID,
    getMatchScoreValue,
} from './helpers';
import styles from './CreatorExploreTable.module.css';
import cn from 'classnames';
import { influencer } from '@round/api';

const SkeletonCell = tableHelpers.SkeletonCell;

const PLATFORM_STAT_COLUMN_SIZE = 115;

type Props = {
    data: influencer.CreatorSearchResult[];
    meta: Meta;
    sorting: SortingState;
    onSortingChange: (sorting: SortingState) => void;
    fetchNextPage: () => void;
    activePreset: string;
    showMatchScore?: boolean;
    /** The creator whose row is expanded to show their profile embed, or `null` if none is. */
    expandedCreatorId?: string | null;
    onRowClick?: (creator: influencer.CreatorSearchResult) => void;
    className?: string;
};

export const CreatorExploreTable = ({
    data,
    meta,
    sorting,
    onSortingChange,
    fetchNextPage,
    activePreset,
    showMatchScore = false,
    expandedCreatorId,
    onRowClick,
    className,
}: Props) => {
    const columns: Column[] = useMemo(
        () => [
            {
                header: 'Creator',
                id: 'creator',
                cell: CreatorCell,
                size: 200,
                enableSorting: false,
            },
            {
                header: 'Match',
                id: MATCH_SCORE_COLUMN_ID,
                accessorFn: getMatchScoreValue,
                cell: SkeletonCell,
                enableSorting: false,
                size: 90,
            },
            {
                header: 'Location',
                id: 'location',
                cell: LocationCell,
                enableSorting: false,
            },
            {
                header: 'Tags',
                id: 'tags',
                cell: TagsCell,
                enableSorting: false,
                size: 200,
            },
            {
                header: 'Draft Time',
                id: 'metrics.avg_draft_time_in_hours',
                meta: { className: styles.muted },
                accessorFn: getDraftTimeValue,
                cell: SkeletonCell,
                size: 110,
            },
            {
                header: 'Response',
                id: 'metrics.response_rate',
                cell: ResponseRateCell,
                size: 110,
            },
            {
                header: 'Last Collab',
                id: 'post_metrics.most_recent_post_date',
                meta: { className: styles.muted },
                accessorFn: getLastCollabValue,
                cell: SkeletonCell,
                size: 110,
            },
            {
                header: 'Platform',
                id: 'platform',
                cell: PlatformSelectorCell,
                meta: {
                    className: styles.platformSelectCell,
                },
                enableSorting: false,
                size: 145,
            },
            {
                header: 'Avg. CPE',
                id: 'estimated_cpe',
                size: PLATFORM_STAT_COLUMN_SIZE,
                cell: AvgCpeCell,
            },
            {
                header: 'Followers',
                id: 'total_follower_count',
                size: PLATFORM_STAT_COLUMN_SIZE,
                cell: FollowersCell,
            },
            {
                header: 'Cost',
                id: 'cost',
                size: PLATFORM_STAT_COLUMN_SIZE,
                cell: CostCell,
            },
            {
                header: 'Plays',
                id: 'avg_video_views_per_post',
                size: PLATFORM_STAT_COLUMN_SIZE,
                cell: PlaysCell,
            },
            {
                header: 'CPM',
                id: 'estimated_cpm',
                size: PLATFORM_STAT_COLUMN_SIZE,
                cell: CpmCell,
            },
            {
                header: 'ER%',
                id: 'avg_engagement_rate',
                size: PLATFORM_STAT_COLUMN_SIZE,
                cell: EnagementRateCell,
            },
            {
                header: 'Posting Freq',
                id: 'post_metrics.activity_rate',
                size: PLATFORM_STAT_COLUMN_SIZE,
                cell: PostingFrequencyCell,
            },
            {
                header: 'Date Added',
                id: DATE_ADDED_COLUMN_ID,
                accessorFn: getDateAddedValue,
                cell: SkeletonCell,
                meta: {
                    className: cn(styles.dateAddedCell, styles.muted),
                },
            },
        ],
        []
    );

    // Match sits outside the presets: it only means something while a blurb search is running.
    const columnVisibility = useMemo(
        () => ({ ...getPresetVisibilityStateFromColumns(activePreset), [MATCH_SCORE_COLUMN_ID]: showMatchScore }),
        [activePreset, showMatchScore]
    );

    const rows = tableHelpers.getLoadingRows(data, { pageSize: PAGE_SIZE, isLoading: meta.isLoading });

    // Expanding is driven off the creator's own id rather than tanstack's row id, since the row a
    // client last expanded is what has to stay expanded across a resort, not whichever index the id
    // now falls on.
    const expandedRowIndex = data.findIndex((creator) => creator._id === expandedCreatorId);
    const expanded: ExpandedState = expandedRowIndex === -1 ? {} : { [expandedRowIndex]: true };

    return (
        <VirtualizedTable
            columns={columns}
            data={rows}
            meta={meta}
            virtualizerOptions={{
                estimateSize: () => 50,
                getItemKey: (index) => rows[index]?._id ?? index,
                overscan: 50,
            }}
            onEndReached={fetchNextPage}
            containerClassName={cn(styles.tableContainer, className)}
            columnPinning={{ left: ['creator'] }}
            enableExpanding
            expanded={expanded}
            // A creator with no account has no profile to embed.
            getRowCanExpand={(row) =>
                !tableHelpers.isLoadingRow(row.original) && getConnectedPlatforms(row.original).length > 0
            }
            renderSubComponent={(row) => {
                const creator = row.original;
                if (tableHelpers.isLoadingRow(creator)) {
                    return null;
                }

                return (
                    <ExpandedCreatorContent
                        creator={creator}
                        selectedPlatform={meta.getRowSelectedPlatform(creator)}
                        onPlatformSelect={(platform) => meta.onRowPlatformSelect(creator._id, platform)}
                        columnVisibility={columnVisibility}
                    />
                );
            }}
            enableSorting
            manualSorting
            sorting={sorting}
            onSortingChange={(newSorting) => onSortingChange(functionalUpdate(newSorting, sorting))}
            enableHiding
            columnVisibility={columnVisibility}
            onRowClick={
                onRowClick &&
                ((row) => {
                    if (!tableHelpers.isLoadingRow(row.original) && row.getCanExpand()) {
                        onRowClick(row.original);
                    }
                })
            }
            noDataLabel={<CreatorTableEmptyState hasError={meta.hasError} />}
        />
    );
};
